

















































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import {
  InputStoryTipSearch,
  InputStoryViewSearch,
  StoryTip,
  StoryView,
} from "@scrile/api-provider/dist/api/StoriesProvider";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import { formatMoneyString, getMainCurrencyCode } from "@/lib/moneyHelpers";
import { StoryFeed, Tab } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseTabs from "@/components/BaseTabs";
import PageTitle from "@/components/PageTitle";
import StoriesModal from "@/components/StoriesModal";
import StoriesStatisticPreviews from "./components/StoriesStatisticPreviews";
import StoryStatisticTipsList from "./components/StoryStatisticTipsList";
import StoryStatisticViewsList from "./components/StoryStatisticViewsList";

export enum StoryStatisticType {
  VIEWS = "views",
  TIPS = "tips",
}

export default defineComponent({
  name: "StoriesStatistic",
  components: {
    StoryStatisticTipsList,
    StoryStatisticViewsList,
    BaseIcon,
    BaseTabs,
    StoriesStatisticPreviews,
    PageTitle,
    StoriesModal,
  },
  props: {
    stories: { type: Array as () => StoryFeed[], required: true },
    storyId: { type: String, default: "" },
    tab: { type: String as () => StoryStatisticType, default: StoryStatisticType.VIEWS },
  },
  setup(props) {
    const activeTab = ref(props.tab);
    const activeStoryId = ref(props.storyId || props.stories[0].id);
    const loading = ref(false);
    const tabs: Tab[] = [{ value: StoryStatisticType.VIEWS }, { value: StoryStatisticType.TIPS }];

    const {
      enableScroll: enableScrollViews,
      items: views,
      getNextPage: getNextViews,
      getFirstPage: getFirstPageViews,
    } = usePager<StoryView, InputStoryViewSearch>((page, data) =>
      providers.StoriesProvider.searchStoryViews({ data: { page, sort: "LIKED,DESC;TIME,DESC", ...data } }, [
        "storyId",
        "userId",
        "liked",
        { user: ["id", "screenName", "username", "role", { avatar: ["urlPart"] }] },
      ])
    );

    const {
      enableScroll: enableScrollTips,
      data: tipsResult,
      items: tips,
      getNextPage: getNextTips,
      getFirstPage: getFirstPageTips,
    } = usePager<StoryTip, InputStoryTipSearch>((page, data) =>
      providers.StoriesProvider.searchStoryTip({ data: { page, sort: "AMOUNT,DESC;USER_ID", ...data } }, [
        "storyId",
        "userId",
        "amount",
        "liked",
        { user: ["id", "screenName", "username", "role", { avatar: ["urlPart"] }] },
      ])
    );

    const activeStory = computed<StoryFeed | undefined>(() =>
      props.stories.find(s => s.id === String(activeStoryId.value))
    );
    const likes = computed(() => activeStory.value?.statistics?.likeCount ?? 0);
    const totalTips = computed(() => tipsResult.value?.totalRecords ?? 0);
    const formatTips = (amount: number) => formatMoneyString(amount, { currencyCode: getMainCurrencyCode() });

    watch(
      activeStoryId,
      async () => {
        try {
          loading.value = true;
          await Promise.all([
            getFirstPageViews({ storyIds: [activeStoryId.value] }),
            getFirstPageTips({ storyId: activeStoryId.value }),
          ]);
        } finally {
          loading.value = false;
        }
      },
      { immediate: true }
    );

    return {
      views,
      tips,
      likes,
      totalTips,
      tabs,
      activeTab,
      loading,
      activeStoryId,
      activeStory,
      enableScrollViews,
      enableScrollTips,
      StoryStatisticType,
      getNextViews,
      getNextTips,
      formatTips,
    };
  },
});
